import { FC, useRef, useState } from 'react';
import triggerTracking from '@blueprint/tracking';
import { Box } from '@zoopla-eng/zds-components/box';
import { EmailMedium } from '@zoopla-eng/zds-components/icons';

import { AuthModalMode, SocialAuthProps } from '../../AuthModal.types';
import SocialAuth from '../SignIn/SocialAuth';
import { useSetUser } from '@zoopla/user';
import { questionsMap, YourSituation } from '@zoopla/onboarding/your-situation';
import { User } from '../../../../graphql.generated';
import {
    StyledIntroText,
    StyledRegisterButton,
    StyledRegisterContainer,
    StyledSignInButton,
    StyledSubtitle
} from './SituationQuickAccess.styles';
import { liverampAts } from '../../utils/liverampAts';
import { useSaveGuestSituationData } from '../../hooks/useSaveGuestSituationData';

const socialCopy = {
    facebook: 'Continue with Facebook',
    google: 'Continue with Google'
};

export const eventCategory = 'registration signin quick access';

export type SituationQuickAccessProps = Pick<
    SocialAuthProps,
    'close' | 'actionAfterAuth' | 'onSocialAuth'
> & {
    selectMode: (newMode: AuthModalMode) => void;
    introText: string;
    isInExperiment?: boolean;
    pageType?: 'for-sale' | 'to-rent';
};

export const SituationQuickAccess: FC<
    React.PropsWithChildren<SituationQuickAccessProps>
> = ({
    close,
    actionAfterAuth,
    onSocialAuth,
    selectMode,
    introText,
    pageType
}) => {
    const setUser = useSetUser();
    const [yourSituationDone, setYourSituationDone] = useState(false);
    const [isFirstQuestionFilled, setIsFirstQuestionFilled] = useState(false);
    const introRef = useRef<HTMLParagraphElement>(null);
    const { getSavedAnswers } = useSaveGuestSituationData();

    const onSocialAuthSuccess = async (data?: User) => {
        if (data) {
            if (data.email) {
                liverampAts(data.email);
            }

            setUser(data);
        }

        if (onSocialAuth) {
            onSocialAuth(data);
        } else {
            close();
        }
    };

    const onSocialClickTracking = (type: 'facebook' | 'google') => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: `click_to_${type}`,
            eventLabel: actionAfterAuth?.eventLabel || 'register_modal'
        });
    };

    const passedActionAfterAuth = {
        action: onSocialAuthSuccess,
        eventLabel: actionAfterAuth?.eventLabel || 'register_modal',
        segment: actionAfterAuth?.segment
    };

    const selectModeAndTrack: SituationQuickAccessProps['selectMode'] = (
        journey
    ) => {
        selectMode(journey);

        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: `click_to_${
                journey === 'register' ? 'register' : 'email_signin'
            }`,
            eventLabel: actionAfterAuth?.eventLabel || 'register_modal'
        });
    };

    const showButtons = () => {
        setYourSituationDone(true);
        setTimeout(() => {
            introRef.current?.focus();
        }, 200);
    };

    const getInitialYourSituationValues = () => {
        if (pageType !== 'for-sale' && pageType !== 'to-rent') {
            return undefined;
        }

        return {
            situationWhatIsYourCurrentPropertyGoal:
                pageType === 'for-sale' ? 'buy_a_property' : 'rent_a_property'
        };
    };

    const onFirstQuestionFilled = () => setIsFirstQuestionFilled(true);

    const saveDataToStorage = async (
        data: Record<string, string | null | undefined>
    ) => {
        const existingAnswers = JSON.parse(getSavedAnswers() || '{}');

        sessionStorage.setItem(
            'unsavedSituationAnswers',
            JSON.stringify({
                ...existingAnswers,
                ...data
            })
        );

        return new Promise<void>((res) => res());
    };

    return (
        <>
            {isFirstQuestionFilled && (
                <Box pb="$S400">
                    <StyledSubtitle>My situation</StyledSubtitle>
                </Box>
            )}
            <YourSituation
                analyticsPage={
                    `/${pageType}/` || actionAfterAuth?.eventLabel || ''
                }
                questionsMap={questionsMap}
                firstQuestionId="situationWhatIsYourCurrentPropertyGoal"
                onResults={showButtons}
                initialValues={getInitialYourSituationValues()}
                forceFormMode="force_edit"
                onFirstQuestionSaved={onFirstQuestionFilled}
                saveData={saveDataToStorage}
            />
            {yourSituationDone && (
                <>
                    <StyledIntroText tabIndex={-1} ref={introRef}>
                        {introText}
                    </StyledIntroText>
                    <SocialAuth
                        copy={socialCopy}
                        type="signin"
                        actionAfterAuth={passedActionAfterAuth}
                        variant="vertical"
                        onClickTracking={onSocialClickTracking}
                    />
                    <StyledSignInButton
                        category="secondary"
                        icon={<EmailMedium />}
                        onClick={() => selectModeAndTrack('sign-in')}
                        data-testid="quick-access-signin"
                    >
                        Sign in with your email
                    </StyledSignInButton>
                    <StyledRegisterContainer>
                        <StyledIntroText>
                            No account?{' '}
                            <StyledRegisterButton
                                category="tertiary"
                                onClick={() => selectModeAndTrack('register')}
                                data-testid="quick-access-register"
                            >
                                Register
                            </StyledRegisterButton>
                        </StyledIntroText>
                    </StyledRegisterContainer>
                </>
            )}
        </>
    );
};
